import axios from 'axios'

import { IUser } from '~/types/user-info-types'
import { homApi } from '~/redux/apis/hom'
import { UserUpdateFields } from '~/onboarding/onboarding.types'
import { AvatarMetadata } from '~/homebase/types/Homebase.types'
import { getFirebase } from '~/firebase'
import { femGlb, mascGlb } from '~/shared/constants/userAvatar.const'
import { USER_API_TAGS } from '~/redux/constants/tags.const'
import { UpdateUserDataReqFields } from '~/settings-scenario/types'
import {
  ReferralCouponsResult,
  ReferralGiftTokenStatus,
  SendReferralCouponsBody,
} from '~/components/Referral/Referral.types'

import { getReadyPlayerMeAvatarUrl, isReadyPlayerMeAvatarUrl } from '~/dashboard/hooks/util'

import { invalidateOnSuccess } from '../utils/util'

import { Result, SuccessResult } from './types'

interface ReadyPlayerMeMetadata {
  id: string
  bodyType: string
  outfitGender: 'masculine' | 'feminine'
  outfitVersion: number
  skinTone?: string
  createdAt?: string
  updatedAt?: string
}

interface AvatarManifest {
  rpm_metadata?: ReadyPlayerMeMetadata
  run_id?: string
  rpm_key?: string
  user_id?: string
  original_avatar_url?: string
  generated_at?: string
  status?: string
  is_template?: boolean
}

// TODO: move this method to  the proper place
export const getRpmMetadata = async (avatarUrl: string) => {
  try {
    const manifestUrl = getReadyPlayerMeAvatarUrl(avatarUrl, 'manifest', '.json')
    const response = await axios.get<AvatarManifest>(manifestUrl).catch(() => null)
    return response?.data
  } catch (_e) {
    return null
  }
}

export const userApi = homApi.injectEndpoints({
  endpoints: (builder) => ({
    getMe: builder.query<IUser, void>({
      query: () => '/v1/users/me',
      transformResponse(response) {
        return response.data
      },
      providesTags: [USER_API_TAGS.UserMe],
    }),

    getAvatarMetadata: builder.query<AvatarMetadata, IUser>({
      queryFn: async (user) => {
        if (!user || !user.avatarUrl) return
        const { id, avatarUrl } = user
        if (femGlb.some((avatar) => avatarUrl.endsWith(avatar))) {
          return {
            data: {
              avatarUrl,
              bodyType: 'fullbody',
              outfitGender: 'feminine',
              outfitVersion: 1,
            },
          }
        } else if (mascGlb.some((avatar) => avatarUrl.endsWith(avatar))) {
          return {
            data: {
              avatarUrl,
              bodyType: 'fullbody',
              outfitGender: 'masculine',
              outfitVersion: 1,
            },
          }
        } else if (isReadyPlayerMeAvatarUrl(avatarUrl)) {
          // XXX: all the migrated avatar urls are S3 ReadyPlayerMe path, so we need to convert them to CDN URLs
          const cdnUrl = getReadyPlayerMeAvatarUrl(avatarUrl, undefined, '.glb')
          const { rpm_metadata: rpmMetadata } = (await getRpmMetadata(avatarUrl)) ?? {}

          return {
            data: {
              avatarUrl: cdnUrl,
              bodyType: rpmMetadata?.bodyType || 'fullbody',
              outfitGender: rpmMetadata?.outfitGender || 'feminine',
              outfitVersion: rpmMetadata?.outfitVersion || 1,
            },
          }
        } else {
          // this is for the legacy avatar support, in case we need to fetch the metadata from firestore for old avatars
          const { getDoc, doc, fireStore } = await getFirebase()
          const snapshot = await getDoc(doc(fireStore, 'avatarMetadata', id.toString()))
          const metadata = snapshot.data() || {
            avatarUrl,
            bodyType: 'fullbody',
            outfitGender: 'feminine',
            outfitVersion: 1,
          }
          return { data: { ...metadata, avatarUrl: user.avatarUrl } }
        }
      },
      providesTags: [USER_API_TAGS.Avatar],
    }),

    updateUser: builder.mutation<SuccessResult<IUser>, { data: UserUpdateFields | Partial<UpdateUserDataReqFields> }>({
      query: ({ data }) => ({
        url: '/v1/users',
        method: 'PATCH',
        body: data,
      }),
      transformResponse: (response: SuccessResult<IUser>) => {
        if (response.type === 'success') {
          if (response.data?.oauth) {
            const [oauthUser] = response.data.oauth

            delete response.data.oauth

            if (oauthUser && oauthUser.id) {
              delete oauthUser.id
            }

            return { ...response, data: { ...response.data, ...oauthUser } }
          }
          return response
        }
        return response
      },
      invalidatesTags: invalidateOnSuccess([
        USER_API_TAGS.UserMe,
        USER_API_TAGS.UserStreak,
        USER_API_TAGS.Avatar,
        USER_API_TAGS.OnboardingProgress,
      ]),
    }),

    userRole: builder.mutation<IUser, { userId: number; role: string }>({
      query: (data) => ({
        url: `/v1/users/${data.userId}/role`,
        method: 'PATCH',
        body: { role: data.role },
      }),
    }),

    setUserTimeZone: builder.mutation<Result<IUser>, { timezone: string }>({
      query: (data) => ({
        url: `/v1/users/timezone`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: invalidateOnSuccess([USER_API_TAGS.UserMe, USER_API_TAGS.UserStreak]),
    }),

    getStreak: builder.query<{ streakDays: number }, void>({
      query: () => ({
        url: '/v1/users/streak',
        method: 'get',
        params: { date: new Date().toISOString() },
      }),
      providesTags: [USER_API_TAGS.UserStreak],
    }),
    sendReferralEmail: builder.mutation<SuccessResult<null>, { email: string; name: string }>({
      query: (data) => ({
        url: `/v1/users/referral`,
        method: 'POST',
        body: data,
      }),
    }),
    verifyReferralGiftToken: builder.query<SuccessResult<ReferralGiftTokenStatus>, string>({
      query: (token) => ({
        url: `/v1/users/referral/gifts/${encodeURIComponent(token)}`,
        method: 'GET',
      }),
    }),
    sendReferralGifts: builder.mutation<
      SuccessResult<ReferralCouponsResult>,
      SendReferralCouponsBody & { token: string }
    >({
      query: ({ token, ...body }) => ({
        url: `/v1/users/referral/gifts/${encodeURIComponent(token)}`,
        method: 'POST',
        body,
      }),
    }),

    dismissReferralPopup: builder.mutation<void, void>({
      query: () => ({
        url: '/v1/users/referral-popup-dismissed',
        method: 'PATCH',
      }),
      invalidatesTags: invalidateOnSuccess([USER_API_TAGS.UserMe]),
    }),
  }),
})

export const {
  useLazyGetMeQuery,
  useUpdateUserMutation,
  useUserRoleMutation,
  useGetStreakQuery,
  useSetUserTimeZoneMutation,
  useGetAvatarMetadataQuery,
  useGetMeQuery,
  useSendReferralEmailMutation,
  useVerifyReferralGiftTokenQuery,
  useSendReferralGiftsMutation,
  useDismissReferralPopupMutation,
} = userApi
export const { getMe, updateUser, getStreak } = userApi.endpoints
export const { useQueryState: useGetMeQueryState } = getMe
